// @flow

import { v4 } from 'uuid';
import queryString from 'query-string';
import React, { Fragment, useCallback, useState } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from '@riseart/antd-provider';
import { Button, Heading, MediaQuery } from '@riseart/common';
import { GridSortSelect } from '@riseart/grid';
import { Wrapper } from '@riseart/layout';
import { FiltersIcon } from '@riseart/icons';
import { artist as ARTIST_ENUM, filter as FILTER_ENUM } from 'Enum';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import HOCFilterPageRoute from 'shared_hocs/routes/FilterPageRoute';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { ArtistArtworkFluidGrid } from 'shared_components/artist/containers/grid/Fluid';
import { HOCSort } from 'shared_hocs/common/Sort';
import { ArtistArtDetailLink } from 'shared_components/artist/art/detail/Link';
import { HOCFilterContainer } from 'shared_hocs/filters/Container';
import { ArtistArtFiltersList } from 'shared_components/artist/containers/filters/Art';

import { raScreenMd, raVisibilityHidden } from '@riseart/antd-provider/dist/website/variables.less';
import {
  artistArtGridHeaderCls,
  artistArtGridSeparatorCls,
  artistArtGridToggleFiltersCls,
} from 'shared_components/artist/art/gallery/Grid.less';

type Props = {
  loading: boolean,
  items: Object[],
  pagination: Object,
  viewSwitcher?: Node,
  match: Object,
  location: Object,
  history: Object,
  domain: string,
  paginationType: string,
  currentLocale: Object,
  artistData: Object,
  artistMenuItems: Array<Object>,
  name: string,
  artistUrl: string,
  selectedArtDetailsId: number,
  urlParams: Object,
  artistSubpageKey: string,
};

const PRELOADER_SUBSCRIBER_IDS = v4();
const ArtistArtSortSelect = HOCSort(GridSortSelect);

const HOCArtsFiltersContainer = HOCFilterPageRoute(
  HOCFilterContainer(ArtistArtFiltersList, {
    domain: FILTER_ENUM.domain.ARTIST_ART,
  }),
  FILTER_ENUM.domain.ARTIST_ART,
);

/**
 * ArtistArtGrid
 */
export const ArtistArtGrid = (props: Props) => {
  const {
    loading,
    items,
    pagination,
    location,
    history,
    artistData,
    domain,
    urlParams,
    artistSubpageKey,
    viewSwitcher = null,
    selectedArtDetailsId,
  } = props;
  const [hideFilters, setHideFilters] = useState(true);
  const { formatMessage } = useIntl();
  const { page, sort, ...rest } = queryString.parse(location.search) || {};
  const parsedPage = parseInt(page, 10);
  const requestedPage = parsedPage > 0 ? parsedPage : 1;
  const requestedSort = Object.keys(ARTIST_ENUM.art.rank).some(
    (key) => ARTIST_ENUM.art.rank[key] === sort,
  )
    ? sort
    : ARTIST_ENUM.art.rank.RANKING_ARTIST;
  const filterParams = {
    ...rest,
    ...(requestedPage > 1 ? { page: requestedPage } : {}),
    ...(requestedSort !== ARTIST_ENUM.art.rank.RANKING_ARTIST ? { sort: requestedSort } : {}),
  };

  const handleShowFiltersClick = useCallback(() => {
    setHideFilters(!hideFilters);
  }, [hideFilters]);

  // Custom url assembler for sort
  // eslint-disable-next-line
  const sortUrlAssembler = (options?: Object = {}, selectedSortValue) => {
    const { sort, ...restFilterParams } = filterParams;
    return UrlAssembler.artistSubpage(artistSubpageKey, artistData.id, artistData.alias, {
      search: {
        ...restFilterParams,
        ...(selectedSortValue ? { sort: selectedSortValue } : null),
      },
    });
  };

  return (
    <MediaQuery minWidth={raScreenMd}>
      {(isScreenMd) => (
        <Fragment>
          <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_IDS} attach={loading} />
          <Wrapper {...(isScreenMd ? null : { noPadding: true })} sizes={{ xs: 'fullWidth' }}>
            <Row justify="space-between" gutter={16} className={artistArtGridHeaderCls}>
              <Col
                className={artistArtGridToggleFiltersCls}
                align="center"
                span={!isScreenMd ? 24 : null}
                {...(isScreenMd ? null : { order: 2 })}
              >
                <Button
                  type="link"
                  linkType="light"
                  onClick={handleShowFiltersClick}
                  icon={<FiltersIcon />}
                >
                  <span>
                    {formatMessage({
                      id: hideFilters
                        ? 'components.filters.showFilters'
                        : 'components.filters.hideFilters',
                    })}
                  </span>
                </Button>
              </Col>
              <Col align="center" span={!isScreenMd ? 24 : null}>
                <Heading level="3">
                  {formatMessage({ id: 'components.artist.artworks' }, { name: artistData.name })}
                </Heading>
              </Col>
              {isScreenMd ? <Col>{viewSwitcher}</Col> : null}
            </Row>
          </Wrapper>
          {hideFilters ? <hr className={artistArtGridSeparatorCls} /> : null}
          <div className={hideFilters ? raVisibilityHidden : null}>
            <HOCArtsFiltersContainer
              {...props}
              urlParams={urlParams}
              domain={domain}
              additionalFilterQueryParams={{ artistId: parseInt(artistData.id, 10) }}
            />
          </div>
          <ArtistArtworkFluidGrid
            includeLDJSON
            sortComponent={
              <ArtistArtSortSelect
                urlAssembler={sortUrlAssembler}
                label={formatMessage({ id: 'components.art.sort' })}
                history={history}
                options={ARTIST_ENUM.art.rank}
                defaultOption={ARTIST_ENUM.art.rank.RANKING_ARTIST}
                currentValue={requestedSort}
                formatMessage={formatMessage}
              />
            }
            layoutSwitchComponent={null}
            loading={loading}
            lazyloadAfter={10}
            items={items}
            pagination={pagination}
            // Do not trigger a page change if items are not loaded yet
            // ('first' and 'last' are used to trigger the items from next/prev pages)
            discardPagination={['first', 'last'].indexOf(selectedArtDetailsId) > -1}
            additionalActionControls={
              isScreenMd
                ? ({ id }: Object) => (
                    <ArtistArtDetailLink
                      url={UrlAssembler.artistSubpage(
                        artistSubpageKey,
                        artistData.id,
                        artistData.alias,
                        {
                          search: filterParams || '',
                          hash: { detail: id },
                        },
                      )}
                      title={formatMessage({ id: 'components.art.openDetailsView' })}
                    />
                  )
                : null
            }
            pageUrl={
              UrlAssembler.artistSubpage(artistSubpageKey, artistData.id, artistData.alias, {
                search: filterParams || '',
                hash: location.hash,
                origin: LocationManager.get('origin'),
              }) || ''
            }
          />
        </Fragment>
      )}
    </MediaQuery>
  );
};
